import type { PropsWithChildren } from "react";
import { useMemo, useEffect, useState } from "react";
import { I18nextProvider } from "react-i18next";
import {
    QueryClient,
    QueryClientProvider
} from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import { getI18nInstance } from "../i18n/config";
import { TelemetryProvider } from "../telemetry/TelemetryProvider";

export const AppProviders = ({
    children
}: PropsWithChildren): JSX.Element => {
    const i18n = useMemo(() => getI18nInstance(), []);
    const queryClient = useMemo(
        () =>
            new QueryClient({
                defaultOptions: {
                    queries: {
                        refetchOnWindowFocus: false,
                        retry: (failureCount, error) => {
                            // Don't retry on cancellation errors (TaskCanceledException, AbortError)
                            if (error && typeof error === "object") {
                                const errorName = error.constructor?.name || "";
                                const errorMessage = error instanceof Error ? error.message : String(error);
                                if (
                                    errorName === "AbortError" ||
                                    errorName === "TaskCanceledException" ||
                                    errorMessage.includes("canceled") ||
                                    errorMessage.includes("aborted")
                                ) {
                                    return false;
                                }
                            }
                            // Don't retry on 4xx errors (client errors)
                            if (error && typeof error === "object" && "response" in error) {
                                const httpError = error as { response?: { status?: number } };
                                const status = httpError.response?.status;
                                if (status && status >= 400 && status < 500) {
                                    return false;
                                }
                                // Don't retry on 5xx errors (server errors like 503) - server is having issues
                                // Retrying will just hammer the server and make things worse
                                if (status && status >= 500) {
                                    return false;
                                }
                            }
                            // Only retry network errors (no response, connection refused, etc.) up to 2 times
                            // But not for server errors (5xx) or client errors (4xx)
                            return failureCount < 2;
                        },
                        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
                        // Keep cached data when query fails (for offline fallback)
                        // Note: placeholderData is used per-query, not globally
                        staleTime: 30000,
                        gcTime: 5 * 60 * 1000 // Keep cache for 5 minutes (formerly cacheTime)
                    }
                }
            }),
        []
    );

    const serviceName =
        import.meta.env.VITE_TELEMETRY_SERVICE_NAME ?? "centerreach-web-client";
    const environment = import.meta.env.VITE_ENVIRONMENT ?? import.meta.env.MODE;
    const collectorUrl = import.meta.env.VITE_OTEL_COLLECTOR_URL;

    // Detect if mobile app is active
    const [isMobileApp, setIsMobileApp] = useState(false);
    
    useEffect(() => {
        // Check if mobile-app-active class exists on body
        const checkMobileApp = () => {
            setIsMobileApp(document.body.classList.contains("mobile-app-active"));
        };
        
        // Initial check
        checkMobileApp();
        
        // Watch for changes
        const observer = new MutationObserver(checkMobileApp);
        observer.observe(document.body, {
            attributes: true,
            attributeFilter: ["class"]
        });
        
        return () => observer.disconnect();
    }, []);

    // Calculate top position for toast container
    const toastTop = useMemo(() => {
        if (isMobileApp) {
            // In mobile app, add padding for iPhone status bar
            // safe-area-inset-top is typically 44px on iPhone with notch
            return "calc(env(safe-area-inset-top, 0px) + 44px + 1rem)";
        }
        return "1rem";
    }, [isMobileApp]);

    return (
        <TelemetryProvider
            serviceName={serviceName}
            environment={environment}
            collectorUrl={collectorUrl} // Re-enabled
        >
            <QueryClientProvider client={queryClient}>
                <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
                <Toaster
                    position="top-right"
                    containerStyle={{
                        top: toastTop
                    }}
                    toastOptions={{
                        duration: 3000,
                        style: {
                            background: "rgba(15, 23, 42, 0.95)",
                            color: "#f8fafc",
                            border: "1px solid rgba(148, 163, 184, 0.2)",
                        },
                        success: {
                            iconTheme: {
                                primary: "#10b981",
                                secondary: "#f8fafc",
                            },
                        },
                        error: {
                            iconTheme: {
                                primary: "#ef4444",
                                secondary: "#f8fafc",
                            },
                        },
                    }}
                />
            </QueryClientProvider>
        </TelemetryProvider>
    );
};